/**
 * <cart-upsell> — dynamic suggested products in the cart drawer.
 * Loads on first drawer open (zero page-load cost). 3-tier source:
 *   1) related to most-recent cart item  2) recently viewed  3) fallback collection (top sellers).
 * Prices: recommendations.json & /products/x.js are in CENTS; /collections/x/products.json is in DOLLARS (×100).
 */
if (!customElements.get('cart-upsell')) {
  class CartUpsell extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.cart-upsell--track');
      this.limit = parseInt(this.dataset.limit || '8', 10);
      this.fallback = this.dataset.fallback || '';
      this._loaded = false;
      this._onOpen = () => this.load();
      document.addEventListener('cart-drawer:open', this._onOpen);
      const d = document.getElementById('Cart-Drawer');
      if (d && d.isOpen) this.load(); // drawer already open during a section re-render
    }
    disconnectedCallback() { document.removeEventListener('cart-drawer:open', this._onOpen); }

    async load() {
      if (this._loaded) return;
      this._loaded = true;
      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        const inCart = new Set((cart.items || []).map(i => i.product_id));
        let out = [];
        if (cart.item_count > 0) {
          const realItems = (cart.items || []).filter(i => !i.properties?._gift);
          const src = realItems.length ? realItems : cart.items;
          const pid = src[src.length - 1].product_id;
          out = await this.recommend(pid);
          if (out.length < this.limit) out = out.concat(await this.recent());
        }
        if (out.length < this.limit && this.fallback) out = out.concat(await this.collection(this.fallback));
        const seen = new Set();
        out = out.filter(p => p && p.variantId && p.available !== false && !inCart.has(p.id) && !seen.has(p.id) && (seen.add(p.id), true)).slice(0, this.limit);
        this.render(out);
      } catch (e) { this._loaded = false; /* non-critical */ }
    }
    async recommend(pid) {
      try { const r = await fetch(`/recommendations/products.json?product_id=${pid}&intent=related&limit=${this.limit}`).then(r => r.json()); return (r.products || []).map(p => this.norm(p, false)); }
      catch (e) { return []; }
    }
    async recent() {
      try {
        const ids = (JSON.parse(localStorage.getItem('dgd_recently_viewed') || '[]')).slice(0, this.limit);
        const ps = await Promise.all(ids.map(h => fetch(`/products/${h}.js`).then(r => r.ok ? r.json() : null).catch(() => null)));
        return ps.filter(Boolean).map(p => this.norm(p, false));
      } catch (e) { return []; }
    }
    async collection(h) {
      try { const r = await fetch(`/collections/${h}/products.json?limit=${this.limit}`).then(r => r.json()); return (r.products || []).map(p => this.norm(p, true)); }
      catch (e) { return []; }
    }
    norm(p, dollars) {
      const v = (p.variants || []).find(x => x.available !== false) || (p.variants || [])[0] || {};
      let price = (p.price != null) ? p.price : v.price;
      let cmp = (p.compare_at_price != null) ? p.compare_at_price : v.compare_at_price;
      if (dollars) { if (price != null) price = Math.round(parseFloat(price) * 100); if (cmp != null) cmp = Math.round(parseFloat(cmp) * 100); }
      let img = p.featured_image || (p.images && p.images[0]) || (p.media && p.media[0] && p.media[0].preview_image && p.media[0].preview_image.src) || '';
      if (img && typeof img === 'object') img = img.src || '';
      return { id: p.id, title: p.title, url: p.url || ('/products/' + p.handle), available: p.available, variantId: v.id, price, cmp, img };
    }
    render(list) {
      if (!list.length) { this.hidden = true; return; }
      this.track.innerHTML = list.map(p => this.card(p)).join('');
      this.hidden = false;
    }
    card(p) {
      const onSale = p.cmp && p.cmp > p.price;
      return `<a href="${p.url}" target="_blank" rel="noopener" class="cart-upsell--card" role="listitem" aria-label="View ${this.esc(p.title)}">
        <div class="cart-upsell--media">${p.img ? `<img loading="lazy" width="56" height="56" src="${this.sized(p.img, 120)}" alt="">` : ''}</div>
        <div class="cart-upsell--meta">
          <span class="cart-upsell--name">${this.esc(p.title)}</span>
          <span class="cart-upsell--price">${onSale ? `<s>${this.money(p.cmp)}</s> ` : ''}<span class="cart-upsell--now">${this.money(p.price)}</span></span>
        </div>
        <span class="cart-upsell--view">View &rsaquo;</span>
      </a>`;
    }
    money(c) {
      if (c == null) return '';
      try { if (window.theme && theme.moneyFormat && window.Shopify && Shopify.formatMoney) return Shopify.formatMoney(c, theme.moneyFormat); } catch (e) {}
      return '$' + (c / 100).toFixed(2);
    }
    sized(u, w) { if (!u) return u; u = u.split('?')[0]; return u.replace(/\.(jpg|jpeg|png|webp|gif)$/i, '_' + w + 'x.$1'); }
    esc(s) { return (s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])); }
  }
  customElements.define('cart-upsell', CartUpsell);
}

/* Toggle html.cart-drawer-open while the drawer is open (used to hide the Shopify chat + lock scroll). */
(function () {
  var drawer = document.getElementById('Cart-Drawer');
  if (!drawer) return;
  var apply = function () {
    var open = !drawer.hasAttribute('inert');
    document.documentElement.classList.toggle('cart-drawer-open', open);
  };
  new MutationObserver(apply).observe(drawer, { attributes: true, attributeFilter: ['inert'] });
  apply();
})();
